import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Alert, Flex } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { ApkInfoWithTargets } from 'client/types';
import { getIsAdmin } from 'client/utils';
import * as keyset from 'i18n/ApkFormFields';
import { v4 as uuidv4 } from 'uuid';

import { ApkFormField } from './ApkFormField';
import styles from './ApkFormFields.module.scss';

interface Props {
    apkInfos: ApkInfoWithTargets[];
    slug: string;
    packageName?: string;
    disabled: boolean;
    onApkChange?: (value: ApkInfoWithTargets[]) => void;
    onPackageNameChange?: (value: string) => void;
    errors?: string[];
    targetPlatforms?: string[];
}

const t = i18n(keyset);

export const ApkFormFields: FC<Props> = ({
    apkInfos,
    slug,
    disabled,
    onApkChange,
    packageName,
    onPackageNameChange,
    targetPlatforms = [],
    errors = [],
}) => {
    const [apkWasUpdated, setApkWasUpdated] = useState<boolean[]>([]);
    const [isApkLoading, setIsApkLoading] = useState(false);

    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    const isAdmin = getIsAdmin();

    useEffect(() => {
        const versionsCodesWasUpdated = apkInfos.map((apkInfo) => !apkInfo?.versionCode);
        setApkWasUpdated(versionsCodesWasUpdated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp]);

    const handleUpdate = useCallback(
        (index: number, updatedValues: Partial<ApkInfoWithTargets>) => {
            const apkInfosUpdated: ApkInfoWithTargets[] = [...apkInfos];
            apkInfosUpdated.splice(index, 1, {
                ...apkInfos[index],
                ...updatedValues,
            });
            onApkChange && onApkChange(apkInfosUpdated);
            if (updatedValues?.packageName && onPackageNameChange) {
                onPackageNameChange(updatedValues.packageName);
            }
        },
        [apkInfos, onApkChange, onPackageNameChange],
    );

    const handleAddApk = () => {
        onApkChange &&
            onApkChange([
                ...apkInfos,
                {
                    appVersionId: undefined,
                    url: undefined,
                    versionNotes: undefined,
                    versionCode: undefined,
                    versionName: undefined,
                    targetPlatforms: [],
                    _creationId: uuidv4(),
                },
            ]);
    };

    const handleDelete = useCallback(
        (index: number) => {
            const updatedApkInfos = [...apkInfos];
            updatedApkInfos.splice(index, 1);
            onApkChange && onApkChange(updatedApkInfos);
        },
        [apkInfos, onApkChange],
    );

    const usedPlatformsSet = new Set(apkInfos.flatMap((apkInfo) => apkInfo.targetPlatforms));
    const availableTargetPlatforms = [...targetPlatforms].sort().filter((platform) => !usedPlatformsSet.has(platform));

    return (
        <>
            {apkInfos.map((apkInfo, index) => (
                <ApkFormField
                    key={apkInfo._creationId}
                    index={index}
                    apkInfo={apkInfo}
                    apkInfosLength={apkInfos.length}
                    slug={slug}
                    packageName={packageName}
                    handleUpdate={handleUpdate}
                    handleDelete={handleDelete}
                    setApkWasUpdated={setApkWasUpdated}
                    apkWasUpdated={apkWasUpdated[index]}
                    availableTargetPlatforms={availableTargetPlatforms}
                    isApkLoading={isApkLoading}
                    setIsApkLoading={setIsApkLoading}
                    error={errors[index]}
                    disabled={disabled}
                />
            ))}
            {isAdmin && !disabled && (
                <Alert
                    theme="info"
                    key="add-apk-alert"
                    title={<span className={styles.apkFormFields__alertTitle}>{t('add-apk-title')}</span>}
                    message={<span className={styles.apkFormFields__message}>{t('add-apk-description')}</span>}
                    className={styles.apkFormFields__alertContainer}
                    actions={
                        <Flex>
                            <Alert.Action onClick={handleAddApk} loading={isApkLoading}>
                                {t('add-apk-action')}
                            </Alert.Action>
                        </Flex>
                    }
                />
            )}
        </>
    );
};
